'use client';
import React, { useEffect } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser';
import hljs from 'highlight.js/lib/core';
import { StyledHtmlContent } from './styled';
import python from 'highlight.js/lib/languages/python';
import 'highlight.js/styles/atom-one-dark.css';
hljs.registerLanguage('python', python);

interface HtmlContentBlockProps {
  html: string;
  className?: string;
}

function convertStyleToObject(styleString: string) {
  const styleObject: any = {};
  styleString.split(';').forEach((styleProperty) => {
    let [key, value] = styleProperty.split(':');
    if (key && value) {
      key = key.trim().replace(/-./g, (char) => char[1].toUpperCase()); // Convert to camelCase
      value = value.trim();
      styleObject[key] = value;
    }
  });
  return styleObject;
}

const sanitizeConfig = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
};

const options: HTMLReactParserOptions = {
  replace: (domNode: any) => {
    if (domNode.name === 'video') {
      const { style, height, width, poster, src, preload } = domNode.attribs;

      const styleObject = style ? convertStyleToObject(style) : {};

      return (
        <video
          style={styleObject}
          height={height}
          width={width}
          poster={poster}
          src={src}
          preload={preload}
          loop={true}
          autoPlay={true}
          muted={true}
        >
          {domToReact(domNode.children, options)}
        </video>
      );
    }
  },
};

const HtmlContentBlock: React.FC<HtmlContentBlockProps> = ({ html, className }) => {
  useEffect(() => {
    hljs.highlightAll();
  }, []);

  const sanitizedContent = DOMPurify.sanitize(html, sanitizeConfig);

  const contentWithVideoAttributes = parse(sanitizedContent, options);

  return <StyledHtmlContent className={className}>{contentWithVideoAttributes}</StyledHtmlContent>;
};

export default HtmlContentBlock;
